import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE, withCredentials: false });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("mostash_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function setAuthToken(token) {
  if (token) localStorage.setItem("mostash_token", token);
  else localStorage.removeItem("mostash_token");
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/* ---------------------------------------------------------------------------
 * Client-side helpers: hash, compression, metadata probes.
 * ------------------------------------------------------------------------- */
async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function maybeCompressImage(file, { maxDim = 2400, quality = 0.86, threshold = 1.2 * 1024 * 1024 } = {}) {
  if (!file || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;
  if (file.size <= threshold) return file;
  const dataUrl = await new Promise((r, j) => {
    const fr = new FileReader();
    fr.onload = () => r(fr.result); fr.onerror = j; fr.readAsDataURL(file);
  });
  const img = await new Promise((r, j) => {
    const i = new Image(); i.onload = () => r(i); i.onerror = j; i.src = dataUrl;
  });
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  if (scale === 1 && file.type === "image/jpeg") return file;
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob || blob.size >= file.size) return file;
  const newName = (file.name || "image.jpg").replace(/\.(png|webp|avif|jpg|jpeg)$/i, "") + ".jpg";
  return new File([blob], newName, { type: "image/jpeg" });
}

async function probeImageMeta(file) {
  const dataUrl = await new Promise((r, j) => {
    const fr = new FileReader();
    fr.onload = () => r(fr.result); fr.onerror = j; fr.readAsDataURL(file);
  });
  const img = await new Promise((r, j) => {
    const i = new Image(); i.onload = () => r(i); i.onerror = j; i.src = dataUrl;
  });
  // Dominant colour via 1×1 canvas sample
  const c = document.createElement("canvas");
  c.width = 1; c.height = 1;
  c.getContext("2d").drawImage(img, 0, 0, 1, 1);
  const [r, g, b] = c.getContext("2d").getImageData(0, 0, 1, 1).data;
  const toHex = (n) => n.toString(16).padStart(2, "0");
  return { width: img.width, height: img.height, dominant_color: `#${toHex(r)}${toHex(g)}${toHex(b)}` };
}

async function probeVideoMeta(file) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = URL.createObjectURL(file);
    v.onloadedmetadata = () => {
      resolve({
        width: v.videoWidth || null,
        height: v.videoHeight || null,
        duration: v.duration ? Math.round(v.duration * 1000) / 1000 : null,
      });
      URL.revokeObjectURL(v.src);
    };
    v.onerror = () => resolve({});
  });
}

/**
 * Upload a file to S3 via backend-signed presigned POST, then register the
 * media row (with full metadata + checksum + variants generation server-side).
 *
 * @param {File} file
 * @param {"image"|"video"} resourceType
 * @param {object} opts
 * @param {string} opts.folder
 * @param {string} [opts.weddingSlug]
 * @param {string} [opts.purpose]
 * @param {object} [opts.association] {wedding_id, reel_id, moodboard_id, story_id}
 * @param {string[]} [opts.tags]
 * @param {string} [opts.altText]
 * @param {boolean} [opts.skipDuplicateCheck]
 * @param {(dupe) => Promise<"use"|"replace"|"cancel">} [opts.onDuplicate]
 */
export async function uploadToS3(file, resourceType = "image", opts = {}) {
  const original = file;
  const compressed = resourceType === "image" ? await maybeCompressImage(file) : file;

  // 1. Hash (of the file about to be uploaded, i.e. post-compression bytes)
  let checksum = null;
  try { checksum = await sha256Hex(compressed); } catch (_) {}

  // 2. Duplicate check
  if (checksum && !opts.skipDuplicateCheck) {
    try {
      const { data: d } = await api.get("/media/check-duplicate", { params: { checksum_sha256: checksum } });
      if (d.exists && d.media) {
        if (opts.onDuplicate) {
          const decision = await opts.onDuplicate(d.media);
          if (decision === "cancel") return null;
          if (decision === "use") return d.media;
        } else if (typeof window !== "undefined" &&
                   !window.confirm(`"${d.media.file_name || 'file'}" is already uploaded. Upload again anyway?`)) {
          return d.media;
        }
      }
    } catch (_) { /* non-fatal */ }
  }

  // 3. Probe metadata client-side
  let meta = {};
  try {
    meta = resourceType === "image"
      ? await probeImageMeta(compressed)
      : await probeVideoMeta(compressed);
  } catch (_) { meta = {}; }

  // 4. Ask backend for presigned POST
  const { data: creds } = await api.get("/media/upload-credentials", {
    params: {
      provider: "s3",
      resource_type: resourceType,
      folder: opts.folder || "uploads",
      wedding_slug: opts.weddingSlug || undefined,
      purpose: opts.purpose || undefined,
      file_name: original.name,
      content_type: compressed.type || (resourceType === "video" ? "video/mp4" : "image/jpeg"),
      size: compressed.size,
    },
  });

  // 5. Upload directly to S3
  const form = new FormData();
  Object.entries(creds.fields).forEach(([k, v]) => form.append(k, v));
  form.append("file", compressed);
  const s3Res = await fetch(creds.upload_url, { method: "POST", body: form });
  if (!s3Res.ok) {
    const txt = await s3Res.text().catch(() => "");
    throw new Error(`S3 upload failed (HTTP ${s3Res.status}). ${txt.slice(0, 200)}`);
  }

  // 6. Register in DB
  const { data: media } = await api.post("/media", {
    provider: "s3",
    storage_key: creds.storage_key,
    media_type: resourceType,
    content_type: compressed.type,
    file_name: original.name,
    original_filename: original.name,
    file_size: compressed.size,
    folder: creds.folder,
    purpose: opts.purpose || undefined,
    wedding_id: opts.association?.wedding_id,
    reel_id: opts.association?.reel_id,
    moodboard_id: opts.association?.moodboard_id,
    story_id: opts.association?.story_id,
    tags: opts.tags || undefined,
    alt_text: opts.altText || undefined,
    checksum_sha256: checksum || undefined,
    width: meta.width,
    height: meta.height,
    duration: meta.duration,
    dominant_color: meta.dominant_color,
  });

  return media;
}

/** Helper: pick the best URL for a media object given the target viewport width. */
export function pickMediaUrl(m, vw) {
  if (!m) return "";
  const s = m.sizes || {};
  if (!vw) return s.desktop || m.url || "";
  if (vw <= 480) return s.thumbnail || s.mobile || s.desktop || m.url;
  if (vw <= 768) return s.mobile || s.tablet || s.desktop || m.url;
  if (vw <= 1200) return s.tablet || s.desktop || m.url;
  return s.desktop || s.original || m.url;
}

/** Helper: build a `srcSet` string for a media object. */
export function buildSrcSet(m) {
  if (!m || !m.sizes) return undefined;
  const parts = [];
  if (m.sizes.thumbnail) parts.push(`${m.sizes.thumbnail} 400w`);
  if (m.sizes.mobile)    parts.push(`${m.sizes.mobile} 800w`);
  if (m.sizes.tablet)    parts.push(`${m.sizes.tablet} 1400w`);
  if (m.sizes.desktop)   parts.push(`${m.sizes.desktop} 2000w`);
  return parts.length ? parts.join(", ") : undefined;
}
